import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError, invalidatePrefix } from '../api/client';
import type { PricingRule, PricingScope } from '../api/types';
import { Badge } from '../components/Badge';
import { Card, PageHeader } from '../components/Card';
import { ErrorBanner, LoadingBlock } from '../components/Feedback';
import { Button, Field, Input, Select, Toggle } from '../components/Form';
import { Modal } from '../components/Modal';
import { DataTable } from '../components/Table';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import { money, num, text } from '../lib/format';

export function PricingPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const path = '/admin-api/pricing';
  const query = useQuery<PricingRule[], ApiError>({
    queryKey: ['api', path],
    queryFn: ({ signal }) =>
      api.get<{ defaults: Record<string, number>; list: PricingRule[] }>(path, signal).then((r) => r.list),
    placeholderData: (prev) => prev,
    gcTime: 0,
    staleTime: 0,
    retry: 1,
  });

  const defaultsQuery = useQuery<Record<string, number>, ApiError>({
    queryKey: ['api', 'defaults'],
    queryFn: ({ signal }) =>
      api.get<{ defaults: Record<string, number>; list: PricingRule[] }>(path, signal).then((r) => r.defaults),
    placeholderData: (prev) => prev,
    gcTime: 0,
    staleTime: 0,
    retry: 1,
  });

  const [editOpen, setEditOpen] = useState(false);
  const [editRule, setEditRule] = useState<PricingRule | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [defaultsOpen, setDefaultsOpen] = useState(false);

  // Create/edit form state
  const [scope, setScope] = useState<PricingScope>('global');
  const [clientId, setClientId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [markupPercent, setMarkupPercent] = useState('30');
  const [markupFixed, setMarkupFixed] = useState('0');
  const [minPrice, setMinPrice] = useState('');
  const [fixedPrice, setFixedPrice] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  // Defaults form state
  const [defMarkup, setDefMarkup] = useState('30');
  const [defFixed, setDefFixed] = useState('0');
  const [defMin, setDefMin] = useState('0');
  const [defRound, setDefRound] = useState('1000');
  const [defSaving, setDefSaving] = useState(false);

  const openCreate = () => {
    setScope('global'); setClientId(''); setProjectId(''); setMarkupPercent('30'); setMarkupFixed('0'); setMinPrice(''); setFixedPrice(''); setEnabled(true);
    setEditRule(null);
    setCreateOpen(true);
  };

  const openEdit = (rule: PricingRule) => {
    setEditRule(rule);
    setScope(rule.scope);
    setClientId(rule.client_id ? String(rule.client_id) : '');
    setProjectId(rule.project_id ? String(rule.project_id) : '');
    setMarkupPercent(String(rule.markup_percent));
    setMarkupFixed(String(rule.markup_fixed));
    setMinPrice(rule.min_price !== null ? String(rule.min_price) : '');
    setFixedPrice(rule.fixed_price !== null ? String(rule.fixed_price) : '');
    setEnabled(rule.enabled);
    setCreateOpen(true);
  };

  const save = async () => {
    setSaving(true);
    const body = {
      scope,
      client_id: clientId ? Number(clientId) : null,
      project_id: projectId ? Number(projectId) : null,
      markup_percent: Number(markupPercent) || 0,
      markup_fixed: Number(markupFixed) || 0,
      min_price: minPrice === '' ? null : Number(minPrice),
      fixed_price: fixedPrice === '' ? null : Number(fixedPrice),
      enabled,
    };
    try {
      if (editRule) {
        await api.patch(`/admin-api/pricing/${editRule.id}`, body);
        toast.success('Правило обновлено');
      } else {
        await api.post('/admin-api/pricing', body);
        toast.success('Правило создано');
      }
      setCreateOpen(false);
      void invalidatePrefix(queryClient, '/admin-api/pricing');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rule: PricingRule) => {
    const ok = await confirm({
      title: 'Удалить правило?',
      body: `Правило ${rule.scope} будет удалено.`,
      confirmLabel: 'Удалить',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.del(`/admin-api/pricing/${rule.id}`);
      toast.success('Правило удалено');
      void invalidatePrefix(queryClient, '/admin-api/pricing');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    }
  };

  const openDefaults = () => {
    const d = defaultsQuery.data;
    setDefMarkup(String(d?.markup_percent ?? 30));
    setDefFixed(String(d?.markup_fixed ?? 0));
    setDefMin(String(d?.min_price ?? 0));
    setDefRound(String(d?.round_to ?? 1000));
    setDefaultsOpen(true);
  };

  const saveDefaults = async () => {
    setDefSaving(true);
    try {
      await api.patch('/admin-api/settings', {
        pricing: {
          markupPercent: Number(defMarkup) || 30,
          markupFixedMicro: Math.round((Number(defFixed) || 0) * 1_000_000),
          minPriceMicro: Math.round((Number(defMin) || 0) * 1_000_000),
          roundToMicro: Math.round((Number(defRound) || 0) * 1_000_000),
        },
      });
      toast.success('Настройки цен сохранены');
      setDefaultsOpen(false);
      void invalidatePrefix(queryClient, '/admin-api/pricing');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Ошибка');
    } finally {
      setDefSaving(false);
    }
  };

  const rules = query.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Ценообразование"
        description="Правила наценки: global → project → client → client_project (приоритет по конкретности)"
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={openDefaults}>Настройки по умолчанию</Button>
            <Button variant="primary" onClick={openCreate}>Создать правило</Button>
          </div>
        }
      />

      {defaultsQuery.data ? (
        <Card title="Настройки по умолчанию">
          <div className="flex flex-wrap gap-4 text-sm text-zinc-300">
            <span>Наценка: <strong className="text-zinc-100">{num(defaultsQuery.data.markup_percent)}%</strong></span>
            <span>Фикс. наценка: <strong className="text-zinc-100">{money(defaultsQuery.data.markup_fixed)}</strong></span>
            <span>Мин. цена: <strong className="text-zinc-100">{money(defaultsQuery.data.min_price)}</strong></span>
            <span>Округление до: <strong className="text-zinc-100">{money(defaultsQuery.data.round_to)}</strong></span>
          </div>
        </Card>
      ) : null}

      <Card title="Правила" noPadding>
        {query.error ? (
          <div className="p-3"><ErrorBanner message={query.error.message} onRetry={() => query.refetch()} /></div>
        ) : query.isLoading ? <LoadingBlock /> : rules.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-zinc-500">Правил нет. Создайте первое.</div>
        ) : (
          <DataTable<PricingRule>
            columns={[
              { key: 'scope', header: 'Область', width: '6rem', render: (r) => <Badge tone="info">{r.scope}</Badge> },
              { key: 'client', header: 'Клиент', hide: 'sm', render: (r) => text(r.client) },
              { key: 'project', header: 'Проект', hide: 'sm', render: (r) => text(r.project) },
              { key: 'markup_percent', header: 'Наценка %', align: 'right', render: (r) => num(r.markup_percent) },
              { key: 'markup_fixed', header: 'Фикс.', align: 'right', hide: 'md', render: (r) => money(r.markup_fixed) },
              { key: 'min_price', header: 'Мин. цена', align: 'right', hide: 'lg', render: (r) => r.min_price !== null ? money(r.min_price) : '—' },
              { key: 'fixed_price', header: 'Фикс. цена', align: 'right', hide: 'lg', render: (r) => r.fixed_price !== null ? money(r.fixed_price) : '—' },
              { key: 'enabled', header: 'Вкл', width: '4rem', align: 'center', render: (r) => <Badge tone={r.enabled ? 'success' : 'muted'}>{r.enabled ? 'да' : 'нет'}</Badge> },
              { key: 'actions', header: '', width: '6rem', align: 'right', render: (r) => (
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(r)}>Изменить</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r)}>Удалить</Button>
                </div>
              ) },
            ]}
            rows={rules}
            rowKey={(r) => r.id}
            minWidth="36rem"
          />
        )}
      </Card>

      {/* Create/Edit rule modal */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={editRule ? 'Изменить правило' : 'Создать правило'}
        size="md"
        footer={<><Button variant="ghost" onClick={() => setCreateOpen(false)}>Отмена</Button><Button variant="primary" loading={saving} onClick={save}>{editRule ? 'Сохранить' : 'Создать'}</Button></>}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Область">
            <Select value={scope} onChange={(e) => setScope(e.target.value as PricingScope)} disabled={!!editRule}>
              <option value="global">global</option>
              <option value="project">project</option>
              <option value="client">client</option>
              <option value="client_project">client_project</option>
            </Select>
          </Field>
          <div />
          {(scope === 'client' || scope === 'client_project') && (
            <Field label="ID клиента"><Input value={clientId} onChange={(e) => setClientId(e.target.value)} inputMode="numeric" /></Field>
          )}
          {(scope === 'project' || scope === 'client_project') && (
            <Field label="ID проекта"><Input value={projectId} onChange={(e) => setProjectId(e.target.value)} inputMode="numeric" /></Field>
          )}
          <Field label="Наценка %"><Input value={markupPercent} onChange={(e) => setMarkupPercent(e.target.value)} inputMode="decimal" /></Field>
          <Field label="Фикс. наценка ($)"><Input value={markupFixed} onChange={(e) => setMarkupFixed(e.target.value)} inputMode="decimal" /></Field>
          <Field label="Мин. цена ($)" hint="пусто = не задано"><Input value={minPrice} onChange={(e) => setMinPrice(e.target.value)} inputMode="decimal" placeholder="нет" /></Field>
          <Field label="Фикс. цена ($)" hint="пусто = не задано"><Input value={fixedPrice} onChange={(e) => setFixedPrice(e.target.value)} inputMode="decimal" placeholder="нет" /></Field>
          <div className="sm:col-span-2">
            <Toggle checked={enabled} onChange={setEnabled} label="Включено" />
          </div>
        </div>
      </Modal>

      {/* Defaults modal */}
      <Modal
        open={defaultsOpen}
        onClose={() => setDefaultsOpen(false)}
        title="Настройки цен по умолчанию"
        size="sm"
        footer={<><Button variant="ghost" onClick={() => setDefaultsOpen(false)}>Отмена</Button><Button variant="primary" loading={defSaving} onClick={saveDefaults}>Сохранить</Button></>}
      >
        <div className="grid gap-3">
          <Field label="Наценка (%)"><Input value={defMarkup} onChange={(e) => setDefMarkup(e.target.value)} inputMode="decimal" /></Field>
          <Field label="Фикс. наценка ($)" hint="значение в USD, конвертируется в micro-USD"><Input value={defFixed} onChange={(e) => setDefFixed(e.target.value)} inputMode="decimal" /></Field>
          <Field label="Мин. цена ($)" hint="значение в USD"><Input value={defMin} onChange={(e) => setDefMin(e.target.value)} inputMode="decimal" /></Field>
          <Field label="Округление до ($)" hint="напр. 0.001 = округление до 0.001"><Input value={defRound} onChange={(e) => setDefRound(e.target.value)} inputMode="decimal" /></Field>
        </div>
      </Modal>
    </div>
  );
}
